'use client';

import React from 'react';
import { useScopedI18n } from '@/locales/client';
import { cn } from '@/lib/utils';
import {
  User,
  Home,
  Calendar,
  Clock,
  FileText,
  ShieldCheck,
  Building2
} from 'lucide-react';

interface QuestionnaireAnswerDisplayProps {
  answers: Record<string, any>; // Les réponses du formulaire
}

const QuestionnaireAnswerDisplay: React.FC<QuestionnaireAnswerDisplayProps> = ({
  answers,
}) => {
  const tForm = useScopedI18n('formQuestionnaire.viewAnswers');

  if (!answers || Object.keys(answers).length === 0) {
    return (
      <div className='flex flex-col items-center justify-center p-8 text-center'>
        <FileText className='mb-3 h-12 w-12 text-gray-300' />
        <p className='font-medium text-gray-500'>{tForm('noAnswers')}</p>
      </div>
    );
  }

  const alwaysShowKeys = [
    'userType',
    'leaseTermType',
    'buildingAgeUnder5',
    'tenant65Over',
    'additionalInfo',
    'buildingType',
    'city',
  ];

  // Filtrer pour ne garder que les réponses non nulles ou définies, sauf pour la ville qui doit toujours s'afficher
  const filteredAnswers = Object.entries(answers).filter(
    ([key, value]) => (value !== null && value !== undefined && value !== '') || key === 'city'
  );

  const getIcon = (key: string) => {
    switch (key) {
      case 'userType': return <User className="h-4 w-4" />;
      case 'buildingType': return <Home className="h-4 w-4" />;
      case 'leaseTermType':
      case 'leaseDuration': return <Clock className="h-4 w-4" />;
      case 'buildingAgeUnder5':
      case 'tenant65Over': return <Calendar className="h-4 w-4" />;
      case 'propertyOwnership':
      case 'singleOwnership':
      case 'spouseOwnership': return <ShieldCheck className="h-4 w-4" />;
      case 'additionalInfo': return <FileText className="h-4 w-4" />;
      case 'city': return <Home className="h-4 w-4" />;
      default: return <Building2 className="h-4 w-4" />;
    }
  };

  return (
    <div className='mt-6 grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-1' style={{ scrollbarWidth: 'thin' }}>
      {filteredAnswers.length > 0 ? (
        filteredAnswers
          .filter(
            ([key, value]) =>
              alwaysShowKeys.includes(key) ||
              (key === 'propertyOwnership' &&
                filteredAnswers.find(
                  ([k, v]) => k === 'userType' && v === 'landlord'
                )) ||
              (key === 'singleOwnership' &&
                filteredAnswers.find(
                  ([k, v]) => k === 'userType' && v === 'landlord'
                ) &&
                filteredAnswers.find(
                  ([k, v]) => k === 'propertyOwnership' && v === true
                )) ||
              (key === 'spouseOwnership' &&
                filteredAnswers.find(
                  ([k, v]) => k === 'userType' && v === 'landlord'
                ) &&
                filteredAnswers.find(
                  ([k, v]) => k === 'propertyOwnership' && v === true
                ) &&
                filteredAnswers.find(
                  ([k, v]) => k === 'singleOwnership' && v === false
                )) ||
              (key === 'additionalInfo' && value?.trim()) ||
              (key === 'leaseDuration' &&
                filteredAnswers.find(
                  ([k, v]) => k === 'leaseTermType' && v === 'fixed-term'
                ))
          )
          .map(([key, value]) => {
            let labelKey = key;
            let displayValue = value;

            switch (key) {
              case 'propertyOwnership':
                labelKey = tForm('propertyOwnership');
                displayValue = value ? tForm('yes') : tForm('no');
                break;
              case 'singleOwnership':
                labelKey = tForm('singleOwnership');
                displayValue = value ? tForm('yes') : tForm('no');
                break;
              case 'spouseOwnership':
                labelKey = tForm('spouseOwnership');
                displayValue = value ? tForm('spouse') : tForm('nonSpouse');
                break;
              case 'leaseDuration':
                labelKey = tForm('leaseDuration');
                displayValue = `${value} ${tForm('months')}`;
                break;
              case 'buildingAgeUnder5':
                labelKey = tForm('buildingAge');
                displayValue =
                  value === 'over-5' ? tForm('over5') : tForm('under5');
                break;
              case 'tenant65Over':
                labelKey = tForm('tenant65Over');
                displayValue = value ? tForm('yes') : tForm('no');
                break;
              case 'userType':
                labelKey = tForm('userType');
                displayValue =
                  value === 'landlord'
                    ? tForm('landlord')
                    : value === 'tenant'
                      ? tForm('tenant')
                      : value === 'sub-tenant'
                        ? tForm('subTenant')
                        : tForm('other');
                break;
              case 'leaseTermType':
                labelKey = tForm('leaseTermType');
                displayValue =
                  value === 'fixed-term'
                    ? tForm('fixedTerm')
                    : tForm('undetermined');
                break;
              case 'buildingType':
                labelKey = tForm('buildingType');
                displayValue =
                  value === 'none'
                    ? tForm('none')
                    : value === 'university'
                      ? tForm('university')
                      : value === 'mobile-home'
                        ? tForm('mobileHome')
                        : tForm('lowRent');
                break;
              case 'additionalInfo':
                labelKey = tForm('additionalInfo');
                break;
              case 'city':
                labelKey = tForm('city');
                break;
            }

            return (
              <div
                key={key}
                className='flex flex-col items-start rounded-xl border border-gray-100 bg-gray-50/50 p-4 transition-all hover:border-purple-200 hover:bg-white dark:border-gray-800 dark:bg-gray-900/40 dark:hover:border-purple-900/50'
              >
                <div className='mb-1 flex items-center space-x-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500'>
                  {getIcon(key)}
                  <span>{labelKey}</span>
                </div>
                <div className={cn(
                  'text-sm font-medium text-gray-900 dark:text-gray-100',
                  key === 'additionalInfo' && 'line-clamp-3 text-left italic text-gray-600 dark:text-gray-400'
                )}>
                  {typeof displayValue === 'object'
                    ? JSON.stringify(displayValue)
                    : displayValue !== null && displayValue !== undefined ? displayValue : ''}
                </div>
              </div>
            );
          })
      ) : (
        <p className='py-4 text-center text-gray-500'>{tForm('allEmpty')}</p>
      )}
    </div>
  );
};

export default QuestionnaireAnswerDisplay;
